import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentAsset, contentApi, extractedErrorObject } from '../api/swagger'
import useLoadingContext from './useLoadingContext'
import { useAppSnackbar } from './useAppSnackbar'

export const useFetchWidenAsset = ({
  /** Content asset unique identifier */
  assetKey,
  /** Unique Id of the operationId + component name being awaited. */
  loadingId,
}: {
  assetKey: number
  loadingId: string
}): {
  /** An object with all the parameter specified in {@link ContentAsset} */
  contentAsset: ContentAsset | undefined
  /**
   * Updates the content asset state with a new value.
   * @param {ContentAsset} newValue - The new value for the content asset state.
   */
  updateContentAssetState: (newValue: ContentAsset) => void
  /** Indicates whether the fetch operation is in progress. */
  isLoading: boolean
  /** Indicates whether an error occurred while fetching the content */
  errorFetchingContent: boolean
  /** Initiates the fetch process and adds the loading ID to the context. */
  fetchAsset: () => void
  /** Initiates a re-fetch process by adding the loading ID to the context. */
  refetch: () => void

  /** Informs the caller if the error fetching content is NDA related. **/
  isNDARequired: boolean
} => {
  const { t } = useTranslation()
  const { handleMessage } = useAppSnackbar()

  const [contentAsset, setContentAsset] = useState<ContentAsset>()
  const [isNDARequired, setIsNDARequired] = useState(false)

  const [errorFetchingContent, setErrorFetchingContent] = useState(false)

  const fetchContentAsset = async () => {
    try {
      const fetchedAsset = await contentApi.fetchContentDetail({ assetKey })
      setContentAsset(fetchedAsset)
      setErrorFetchingContent(false)
    } catch (caughtError) {
      setErrorFetchingContent(true)

      const errorObject = (await extractedErrorObject(caughtError)) ?? {
        code: 'Unknown',
        message:
          (caughtError as unknown as Error).message ??
          t(
            'UseFetchWidenAsset.FetchContentDetail.Error',
            'An error occurred while fetching asset.'
          ),
      }

      if (errorObject.code === 'AcknowledgementRequired') {
        setIsNDARequired(true)
      } else {
        handleMessage(errorObject.message, 'error')
      }
    }
  }

  const updateContentAssetState = setContentAsset

  const { isLoading, triggerFetch } = useLoadingContext({
    asyncFunction: fetchContentAsset,
    loadingId,
  })

  return {
    isLoading,
    contentAsset,
    updateContentAssetState,
    fetchAsset: triggerFetch,
    refetch: triggerFetch,
    errorFetchingContent,
    isNDARequired,
  }
}
